// Aufgeteilt aus dem früheren 2600-Zeilen-Monolithen `musicXmlDom.ts` (Import-Pfad unverändert:
// `@/composables/musicXmlDom` zeigt auf `index.ts`). Abhängigkeitsregel im Ordner: geteilte
// Basis-Helfer liegen in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN nicht
// (aktuell: bass→chordInference, notes→core, diatonic→notes, structure→notes,
// lyrics→bass, clipboard→structure, fragment→structure).
import {type LabelColorTarget, parseMusicXml} from './core';

// ============ Auto-erkannte Akkorde ============

/**
 * Farbmarker, mit dem das Backend automatisch erkannte Akkordsymbole **und** die Noten des
 * Bass-Vorschau-Systems taggt (`color="…"`). **Muss Zeichen für Zeichen `INFERRED_CHORD_COLOR` in
 * `griffschrift/chords/inference.py` entsprechen** – der Wert wird vom Backend geschrieben und hier
 * nur wiedererkannt; driftet er auseinander, gilt jedes markierte Element als „vom Nutzer
 * bearbeitet". Guard: `tests/test_engine_invariants_guard.py::test_inferred_marker_mirrors_match`.
 *
 * Vergleiche stets case-insensitiv über `isInferredColor`: music21 exportiert die Farbe
 * großgeschrieben zurück (analog `is_inferred_color` im Backend).
 */
export const INFERRED_CHORD_COLOR = '#26D488';

/**
 * Farbe, mit der **dieses Frontend** die von Hand geschriebenen Takte des Bass-Vorschau-Systems
 * markiert – das Gegenstück zu `INFERRED_CHORD_COLOR`: **grün = maschinell erzeugt, dunkelblau =
 * deine Hand**. Die Engine schreibt denselben Wert auf die Bass-Silben der Ausgabe, die aus dieser
 * Handarbeit entstanden sind (`HANDWRITTEN_BASS_COLOR` in `griffschrift/mapping/bass_layout.py`).
 *
 * Beide Seiten **schreiben** die Farbe nur; erkannt wird Handarbeit weiterhin über das *Fehlen* des
 * grünen Markers. Driftet der Wert, zeigt die Vorschau ein anderes Dunkelblau als der Druck –
 * Guard: `tests/test_engine_invariants_guard.py::test_handwritten_bass_color_mirrors_match`.
 */
export const HANDWRITTEN_BASS_COLOR = '#0e189e';

/**
 * Metadaten-Feld, in dem das Backend vermerkt, **dass** eine Auto-Akkorderkennung gelaufen ist
 * (`mark_chord_inference_state` in `griffschrift/chords/inference.py`). music21 legt es als
 * `<identification><miscellaneous><miscellaneous-field name="…">` ab; der Vermerk reist mit der
 * Datei (überlebt Reload, Download/Re-Upload, History-Restore, Transpose/Merge/Shift).
 */
export const CHORD_INFERENCE_META_FIELD = 'ChordInferenceState';

/**
 * Trägt `color` den Auto-Erkennungs-Marker? **Der einzige erlaubte Vergleich** – Spiegel von
 * `griffschrift/chords/inference.py::is_inferred_color`.
 *
 * Case-insensitiv, weil music21 Farben beim Export großgeschrieben zurückschreibt und die Webapp
 * jeden Score als MusicXML-String durch die Endpunkte reicht. Ein case-sensitiver `===` an einer
 * einzelnen Stelle verlöre den Marker nach jedem Roundtrip still – deshalb geht jeder Konsument hier durch.
 */
export function isInferredColor(color: string | null | undefined): boolean {
    return !!color && color.toLowerCase() === INFERRED_CHORD_COLOR.toLowerCase();
}

/**
 * Anzeigezustand der automatischen Akkorderkennung für **eine** Datei:
 * - `active`   – Auto-Akkorde vorhanden (`count > 0`)
 * - `original` – die Datei bringt **eigene** (nicht auto-erkannte) Akkorde mit, ohne Auto-Akkorde
 * - `empty`    – Erkennung lief, hat aber nichts gesetzt (Empfindlichkeit zu hoch, oder alle Takte
 *                hatten bereits Symbole)
 * - `removed`  – die Auto-Akkorde wurden bewusst wieder entfernt
 * - `never`    – es lief nie eine Erkennung
 */
export type ChordInferenceState = 'never' | 'active' | 'empty' | 'removed' | 'original';

export interface ChordInferenceStatus {
    state: ChordInferenceState;
    /** Anzahl der auto-erkannten Akkordsymbole im Inhalt (live gezählt, nie aus Metadaten). */
    count: number;
}

/**
 * Zählt die automatisch erkannten Akkordsymbole – `<harmony>` mit dem `INFERRED_CHORD_COLOR`-Marker
 * (case-insensitiv). Leeres/ungültiges XML → 0.
 */
export function countInferredChordSymbols(musicXml: string): number {
    if (!musicXml) return 0;
    const doc = parseMusicXml(musicXml);
    let count = 0;
    for (const harmony of doc.querySelectorAll('harmony')) {
        if (isInferredColor(harmony.getAttribute('color'))) count++;
    }
    return count;
}

/**
 * True, wenn das MusicXML mindestens ein automatisch erkanntes Akkordsymbol enthält. Reine,
 * inhaltsbasierte Ground-Truth-Prüfung; leerer/ungültiger XML → false.
 */
export function hasInferredChordSymbols(musicXml: string): boolean {
    return countInferredChordSymbols(musicXml) > 0;
}

/**
 * True, wenn das MusicXML mindestens ein **eigenes** (nicht auto-erkanntes) Akkordsymbol enthält –
 * ein `<harmony>`, dessen Farbe **ungleich** `INFERRED_CHORD_COLOR` ist (case-insensitiv) oder das
 * gar keine Farbe trägt. Spiegelt die Backend-Semantik
 * `chords/inference.py::has_original_chord_symbols` (jedes nicht inferiert-gefärbte
 * `harmony.ChordSymbol` – inkl. N.C. als Subklasse – gilt als „original"). Leerer/ungültiger XML → false.
 */
export function hasOriginalChordSymbols(musicXml: string): boolean {
    if (!musicXml) return false;
    const doc = parseMusicXml(musicXml);
    for (const harmony of doc.querySelectorAll('harmony')) {
        if (!isInferredColor(harmony.getAttribute('color'))) return true;
    }
    return false;
}

/**
 * True, wenn das MusicXML **irgendein** Akkordsymbol enthält – eigenes oder auto-erkanntes.
 * Treibt das Ausblenden des kompakten Erkennungs-Panels in der Vorschau. Leerer/ungültiger XML → false.
 */
export function hasAnyChordSymbols(musicXml: string): boolean {
    if (!musicXml) return false;
    return parseMusicXml(musicXml).querySelectorAll('harmony').length > 0;
}

/**
 * Liest den Erkennungs-Vermerk aus den Score-Metadaten (`null`, wenn nie eine Erkennung lief).
 * Unbekannte Werte werden zu `null` normalisiert.
 */
export function readChordInferenceMeta(musicXml: string): 'applied' | 'removed' | null {
    if (!musicXml) return null;
    const doc = parseMusicXml(musicXml);
    const field = doc.querySelector(`miscellaneous-field[name="${CHORD_INFERENCE_META_FIELD}"]`);
    const value = field?.textContent?.trim();
    return value === 'applied' || value === 'removed' ? value : null;
}

/**
 * Leitet den vollständigen Anzeigezustand aus dem MusicXML ab.
 *
 * **Invariante: der Inhalt gewinnt bei der Anwesenheit; die Metadaten disambiguieren nur den
 * Null-Fall** (kritisch – nicht rückbauen). Priorität: `count > 0 → active` (Auto-Akkorde da) →
 * eigene Akkorde → `original` → Meta `removed`/`applied` → sonst `never`. `active` schlägt
 * bewusst `original` (Gap-Fill: sind Auto-Akkorde vorhanden, ist die Auto-Erkennung die tragende
 * Aussage). Dadurch kann die Anzeige nicht veralten, wenn der Nutzer Akkorde im Quelltext-Editor
 * manuell löscht oder ergänzt: Anwesenheit/Anzahl sind stets aus dem Inhalt gezählt, aus den
 * Metadaten stammt einzig das nicht ableitbare Faktum „eine Erkennung ist gelaufen".
 */
export function chordInferenceStatus(musicXml: string): ChordInferenceStatus {
    const count = countInferredChordSymbols(musicXml);
    if (count > 0) return {state: 'active', count};
    if (hasOriginalChordSymbols(musicXml)) return {state: 'original', count};
    const meta = readChordInferenceMeta(musicXml);
    if (meta === 'removed') return {state: 'removed', count};
    if (meta === 'applied') return {state: 'empty', count};
    return {state: 'never', count};
}


/**
 * Die Takte, deren Akkordsymbole **auto-erkannt** sind – als Adressen für die SVG-Nachfärbung
 * (`composables/osmd/useScoreLabelColors.ts`). Leeres/ungültiges XML → `[]`.
 *
 * **Der Schlüssel ist der Takt, nicht das einzelne Symbol.** Das ist keine Bequemlichkeit, sondern
 * die belastbarere Brücke: die Erkennung füllt nur **symbollose** Takte
 * (`chords/inference.py`), und der Akkord-Editor löscht beim Schreiben alle `<harmony>` des Takts –
 * ein Takt ist deshalb **ganz** inferiert oder **ganz** original. Eine positionsweise Zuordnung
 * XML↔Grafik wäre dagegen unsicher: `<backup>` trennt Dokument- von Zeitreihenfolge, und OSMD
 * überspringt beim Aufbau still jedes `<harmony>` ohne `<root>` (N.C.) – der Index verrutschte.
 *
 * **Gemischter Takt → kein Target.** Sollte die Lückenfüll-Invariante je aufweichen, bleibt der
 * Takt lieber unauffällig grau, statt original gesetzte Akkorde als „auto-erkannt“ auszugeben.
 *
 * Bei mehrstaffigen Parts (`<staves>2</staves>`) werden konservativ alle Staffs des Takts
 * adressiert: `<harmony>` darf ein `<staff>`-Kind tragen, die Griffschrift-Ausgabe und typische
 * Melodie-Uploads haben aber genau eine Notenzeile je Part.
 */
export function inferredChordSymbolTargets(musicXml: string): LabelColorTarget[] {
    if (!musicXml) return [];
    try {
        const doc = parseMusicXml(musicXml);
        const out: LabelColorTarget[] = [];
        let staffAcc = 0;
        for (const part of doc.querySelectorAll('part')) {
            const staves = parseInt(part.querySelector('staves')?.textContent || '1', 10) || 1;
            [...part.querySelectorAll('measure')].forEach((measure, measureIndex) => {
                const symbols = [...measure.querySelectorAll('harmony')];
                if (!symbols.length) return;
                if (!symbols.every(s => isInferredColor(s.getAttribute('color')))) return;
                for (let staff = 0; staff < staves; staff++) {
                    out.push({staffIndex: staffAcc + staff, measureIndex, color: INFERRED_CHORD_COLOR});
                }
            });
            staffAcc += staves;
        }
        return out;
    } catch (e) {
        console.error('Error collecting inferred chord symbol targets:', e);
        return [];
    }
}
